import { useState } from "react";
import { Shield, Award, CheckCircle, Download, ExternalLink } from "lucide-react";
import { certifications } from "../data";

const categories = ["All", "Tax & Legal", "Government", "Legal", "Quality", "Award", "Partnership"];

const catIcons = {
  "Tax & Legal": "📋",
  "Government": "🏛️",
  "Legal": "⚖️",
  "Quality": "🏅",
  "Award": "🏆",
  "Partnership": "🤝",
};

const catColors = {
  "Tax & Legal": { bg: "rgba(27,67,50,0.08)", accent: "#1B4332" },
  "Government": { bg: "rgba(79,70,229,0.06)", accent: "#4338CA" },
  "Legal": { bg: "rgba(107,114,128,0.08)", accent: "#374151" },
  "Quality": { bg: "rgba(212,160,23,0.08)", accent: "#8B6914" },
  "Award": { bg: "rgba(220,38,38,0.06)", accent: "#B91C1C" },
  "Partnership": { bg: "rgba(64,145,108,0.08)", accent: "#2D6A4F" },
};

export default function Certifications() {
  const [cat, setCat] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = cat === "All" ? certifications : certifications.filter(c => c.category === cat);

  return (
    <div>
      {/* Hero */}
      <div className="page-hero">
        <div className="container">
          <span className="eyebrow" style={{ color: "var(--gold-light)" }}>Transparency & Trust</span>
          <h1 className="display-lg" style={{ marginTop: "0.5rem", marginBottom: "0.75rem" }}>Certifications & Awards</h1>
          <p>BCF is fully compliant with all regulatory requirements. Our credentials reflect our commitment to transparency, accountability, and quality.</p>
        </div>
      </div>
      <div className="ribbon-divider" />

      {/* Trust Bar */}
      <div style={{ background: "var(--white)", padding: "2rem 0", borderBottom: "1px solid var(--gray-light)" }}>
        <div className="container">
          <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap", justifyContent: "center", alignItems: "center" }}>
            {[
              ["🔒", "100% Transparent Finances"],
              ["📊", "Audited Accounts"],
              ["✅", "Government Registered"],
              ["🌟", "80G Tax Benefits for Donors"],
              ["🌐", "FCRA Approved"],
            ].map(([icon, label], i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.88rem", fontFamily: "var(--font-label)", fontWeight: 600, color: "var(--forest)" }}>
                <span>{icon}</span> {label}
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {/* Category Filter */}
          <div style={{ background: "var(--white)", borderRadius: "var(--radius-lg)", padding: "1.25rem", marginBottom: "2.5rem", boxShadow: "var(--shadow)" }}>
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
              {categories.map(c => (
                <button key={c} className={`filter-btn ${cat === c ? "active" : ""}`} style={{ padding: "0.45rem 1rem", fontSize: "0.82rem" }} onClick={() => setCat(c)}>
                  {catIcons[c] || ""} {c}
                </button>
              ))}
            </div>
          </div>

          {/* Certificates Grid */}
          <div className="grid-2">
            {filtered.map((cert, i) => {
              const colors = catColors[cert.category] || { bg: "rgba(27,67,50,0.06)", accent: "#1B4332" };
              return (
                <div key={cert.id} className="card" style={{ padding: 0, overflow: "hidden", cursor: "pointer" }} onClick={() => setSelected(cert)}>
                  {/* Top accent strip */}
                  <div style={{ height: 5, background: `linear-gradient(90deg, ${colors.accent}, var(--gold))` }} />
                  <div style={{ padding: "1.75rem 2rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem" }}>
                      <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                        <div style={{ width: 52, height: 52, borderRadius: 12, background: colors.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem", flexShrink: 0 }}>
                          {catIcons[cert.category] || "📜"}
                        </div>
                        <div>
                          <span style={{ fontSize: "0.72rem", fontFamily: "var(--font-label)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: colors.accent }}>{cert.category}</span>
                          <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem", marginTop: "0.15rem", lineHeight: 1.3 }}>{cert.title}</h3>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", flexShrink: 0 }}>
                        <CheckCircle size={16} fill="#22C55E" color="#22C55E" />
                        <span style={{ fontSize: "0.75rem", color: "#16A34A", fontWeight: 700, fontFamily: "var(--font-label)" }}>Verified</span>
                      </div>
                    </div>

                    <p style={{ color: "var(--gray)", fontSize: "0.88rem", lineHeight: 1.7, marginBottom: "1.25rem" }}>{cert.description}</p>

                    {/* Details Grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", padding: "1rem", background: "var(--cream)", borderRadius: 10 }}>
                      {[
                        ["Authority", cert.authority],
                        ["Cert. No.", cert.number],
                        ["Issued", cert.issued],
                        ["Validity", cert.valid],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <div style={{ fontSize: "0.7rem", color: "var(--gray)", fontFamily: "var(--font-label)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0.15rem" }}>{label}</div>
                          <div style={{ fontSize: "0.85rem", color: "var(--charcoal)", fontWeight: 500 }}>{value}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem" }}>
                      <button style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem", color: "var(--forest-mid)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-label)", padding: 0 }}>
                        <Download size={14} /> Download
                      </button>
                      <button style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem", color: "var(--gray)", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-label)", padding: 0 }}>
                        <ExternalLink size={14} /> Verify Online
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div style={{ background: "var(--forest)", borderRadius: "var(--radius-lg)", padding: "2.5rem", textAlign: "center", marginTop: "3rem", color: "var(--white)" }}>
            <Shield size={32} color="var(--gold)" style={{ margin: "0 auto 1rem" }} />
            <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", marginBottom: "0.75rem" }}>Donate with Complete Confidence</h3>
            <p style={{ color: "rgba(255,255,255,0.72)", maxWidth: 520, margin: "0 auto 1.75rem", fontSize: "0.95rem", lineHeight: 1.7 }}>
              Every rupee donated to BCF is accounted for, audited annually, and goes directly to the children. Our 80G certification ensures you also benefit from a 50% tax deduction on your donation.
            </p>
            <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button className="btn btn-primary" style={{ fontSize: "0.95rem" }}>
                <Award size={16} /> Donate & Save Tax
              </button>
              <button className="btn btn-outline" style={{ fontSize: "0.95rem" }}>
                Download Annual Report
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--white)", borderRadius: "var(--radius-lg)", maxWidth: 560, width: "100%", padding: "2.5rem", position: "relative", boxShadow: "0 25px 60px rgba(0,0,0,0.25)" }}>
            <button onClick={() => setSelected(null)} style={{ position: "absolute", top: "1.25rem", right: "1.25rem", background: "var(--gray-light)", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", fontSize: "1.1rem" }}>×</button>
            <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>{catIcons[selected.category]}</div>
            <span className="badge badge-green" style={{ marginBottom: "0.75rem" }}>{selected.category}</span>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", marginBottom: "1rem" }}>{selected.title}</h2>
            <p style={{ color: "var(--gray)", lineHeight: 1.8, marginBottom: "1.5rem" }}>{selected.description}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", padding: "1.25rem", background: "var(--cream)", borderRadius: 12, marginBottom: "1.5rem" }}>
              {[["Issuing Authority", selected.authority], ["Certificate Number", selected.number], ["Date Issued", selected.issued], ["Validity", selected.valid]].map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontSize: "0.72rem", color: "var(--gray)", fontFamily: "var(--font-label)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0.2rem" }}>{l}</div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 600 }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center" }}><Download size={15} /> Download Certificate</button>
              <button className="btn btn-outline" style={{ flex: 1, justifyContent: "center", borderColor: "var(--forest)", color: "var(--forest)" }}><ExternalLink size={15} /> Verify</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
